import React from 'react'
import './edit-journey.scss'
import { PageHeader } from '@pushly/aqe/lib/components'
import { BetterComponent } from '../../../components/better-component/better-component'
import { AppState } from '../../../stores/app'
import { AppService, JourneyService, DomainIntegrationService, DomainService } from '../../../services'
import { Container } from 'typescript-ioc/es5'
import { JourneyBuilderMode } from '../../../components/journey-builder/enums'
import { ifDomainChanged } from '../../../_utils/domain'
import { JourneyWizard } from '../../../components/journey-wizard/journey-wizard'
import { DomainDto } from '../../../dtos/domain'
import { StatusType } from '../../../enums/status-type'
import { UserType } from '../../../enums/user-type'
import { onResponseError403 } from '../../../_utils/on-response-error-403'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { GetDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { JourneyStatusRef } from '../types/journey-nodes'
import { Journey } from '../types/journey-api-response'

interface IEditJourney {}

interface IState {
    loading: boolean
    domain: DomainDto
    loadedJourney?: Journey
    journey?: Journey
    integrations?: GetDomainIntegrationResponse[]
    journeyHasChanged: boolean
}

export class EditJourneyWizard extends BetterComponent<IEditJourney, IState> {
    private readonly appState: AppState
    private readonly appService: AppService
    private readonly domainService: DomainService
    private readonly journeyService: JourneyService
    private readonly integrationsSvc: DomainIntegrationService

    private disposeObservers: any[] = []

    private wizardRef = React.createRef<JourneyStatusRef>()

    public constructor(props: IEditJourney) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)
        this.journeyService = Container.get(JourneyService)
        this.integrationsSvc = Container.get(DomainIntegrationService)

        this.state = {
            loading: false,
            domain: this.appState.currentDomain!,
            journeyHasChanged: false,
        }
    }

    public componentDidMount(): void {
        this.loadResources()

        this.disposeObservers.push(ifDomainChanged(this.appState, () => this.goBackToJourneysList()))
    }

    public componentWillUnmount() {
        super.componentWillUnmount()

        this.disposeObservers.forEach((disposeFn) => disposeFn())
    }

    public render(): React.ReactNode {
        const { loading, journey } = this.state
        const isDraftJourney = journey?.status === StatusType.DRAFT.name
        const headerPrefix = isDraftJourney ? 'Draft' : 'Edit'
        const headerName = !journey ? 'Journey' : journey.name

        return (
            <div className="edit-journey">
                <PageHeader
                    title={`${loading ? 'Loading...' : `${headerPrefix}: ${headerName}`}`}
                    append={<span>ID: {this.journeyId ?? '...'}</span>}
                    onTitleSet={this.appService.customizeTabTitle}
                />

                <JourneyWizard
                    wizardRef={this.wizardRef}
                    loading={this.state.loading}
                    mode={JourneyBuilderMode.EDIT}
                    domain={this.state.domain}
                    journey={this.state.journey}
                    onChange={this.handleUpdate}
                    onSubmit={this.handleSubmit}
                    integrations={this.state.integrations}
                    tabsProps={{
                        destroyInactiveTabPane: true,
                    }}
                    canTabNavigate={() => {
                        if (this.wizardRef.current?.builderHasChanged) {
                            simpleNotification('error', 'You have unsaved changes. Please save before continuing.')
                            return false
                        }

                        return true
                    }}
                />
            </div>
        )
    }

    public get journeyId(): number {
        return this.injectedProps.match.params.journeyId
    }

    protected get templateId(): number {
        return this.queryString.template as any
    }

    protected goBackToJourneysList() {
        this.appService.routeWithinDomain('/journeys')
    }

    protected handleUpdate = async (journey: Journey) => {
        return this.setState({ journey })
    }

    protected handleSubmit = async (): Promise<void> => {
        // reload journey and associated resources
        return this.loadResources()
    }

    protected loadResources = async (): Promise<void> => {
        let domain = this.state.domain
        const user = this.appState.currentUser

        await this.setState({
            journey: undefined,
            loading: true,
        })

        this.appService.setModuleLoading()

        const update: Partial<IState> = { loading: false }

        const loadRequests: Promise<{ ok: boolean; data?: any; cancelled?: boolean }>[] = []

        // fetch domain integrations
        const domainReqId = loadRequests.push(this.domainService.fetchById(domain.id)) - 1
        const integrationsReqId = loadRequests.push(this.integrationsSvc.fetchAll(domain.id)) - 1
        let journeyReqId: number | undefined

        if (this.journeyId) {
            journeyReqId =
                loadRequests.push(
                    this.journeyService.fetchById(this.journeyId, {
                        errorHandler: onResponseError403(() => {
                            this.goBackToJourneysList()
                        }),
                    }),
                ) - 1
        }

        const responses = await Promise.all(loadRequests)

        const domainRes = responses[domainReqId]
        const integrationsRes = responses[integrationsReqId]

        let journeyRes: typeof responses[number] | undefined
        if (journeyReqId) {
            journeyRes = responses[journeyReqId]
        }

        if (domainRes.ok) {
            update.domain = domainRes.data as DomainDto
        }

        if (integrationsRes.ok) {
            update.integrations = integrationsRes.data as GetDomainIntegrationResponse[]
        }

        if (journeyRes?.ok) {
            const journey = journeyRes.data as Journey
            journey.campaign_type = journey.type
            journey.steps = journey.revision?.steps
            update.loadedJourney = journey
            update.journey = journey
        }

        this.appService.unsetModuleLoading()
        this.setState((prev) => ({ ...prev, ...update }))
    }
}
